import { gameConfig } from './config'
import { computeView, render, screenToWorldX, type View } from './render'
import type { SkinColors } from './skins'
import { createGame, type GameEvent, type GameState, startGame, step } from './state'

export interface GameLoop {
  /** Новая партия (из idle или после game over). */
  start(): void
  setSkin(skin: SkinColors): void
  /** Снять rAF и слушатели. Обязательно в cleanup эффекта: StrictMode монтирует дважды. */
  destroy(): void
}

/**
 * Связка чистого step() с браузером: rAF, фиксированный шаг физики, ввод,
 * размер canvas. Наружу — только события (удар, конец партии): React
 * перерисовывается по ним, а не каждый кадр.
 */
export function createGameLoop(
  canvas: HTMLCanvasElement,
  onEvent: (event: GameEvent) => void,
  initialSkin: SkinColors,
): GameLoop {
  const ctx = canvas.getContext('2d')
  const stepDt = gameConfig.physicsStep

  let state: GameState = createGame()
  let targetX = state.paddle.x
  let view: View = computeView(1, 1)
  let accumulator = 0
  let lastTime: number | null = null
  let frameId = 0
  let skin = initialSkin

  function resize(): void {
    const dpr = window.devicePixelRatio || 1
    const rect = canvas.getBoundingClientRect()
    canvas.width = Math.max(1, Math.round(rect.width * dpr))
    canvas.height = Math.max(1, Math.round(rect.height * dpr))
    view = computeView(canvas.width, canvas.height)
  }

  function onPointer(e: PointerEvent): void {
    const rect = canvas.getBoundingClientRect()
    const dpr = canvas.width / (rect.width || 1)
    targetX = screenToWorldX(view, (e.clientX - rect.left) * dpr)
  }

  function frame(now: number): void {
    // Скрытая вкладка останавливает rAF; при возврате не догоняем пропущенное.
    const dt = lastTime === null ? 0 : Math.min((now - lastTime) / 1000, gameConfig.maxFrameTime)
    lastTime = now
    accumulator += dt

    while (accumulator >= stepDt) {
      const result = step(state, { targetX }, stepDt, Math.random)
      state = result.state
      for (const event of result.events) onEvent(event)
      accumulator -= stepDt
    }

    if (ctx) render(ctx, state, view, skin)
    frameId = requestAnimationFrame(frame)
  }

  resize()
  const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(resize)
  observer?.observe(canvas)
  canvas.addEventListener('pointermove', onPointer)
  canvas.addEventListener('pointerdown', onPointer)
  frameId = requestAnimationFrame(frame)

  return {
    start() {
      state = startGame(state)
    },
    setSkin(next) {
      skin = next
    },
    destroy() {
      cancelAnimationFrame(frameId)
      observer?.disconnect()
      canvas.removeEventListener('pointermove', onPointer)
      canvas.removeEventListener('pointerdown', onPointer)
    },
  }
}
